import { useState, useEffect, useCallback, useMemo } from 'react';
import { TextSelect, Eye, Pencil } from 'lucide-react';
import { useDebouncedCallback } from 'use-debounce';
import { isExecutableSelect, isExecutableStatement } from '../../common/utils/peekResultsDetection';
import type { SqlMonacoHandle } from '../../common/utils/sqlMonacoTypes';
import type { EditorSelectionRange } from '@/types/aiWorkspace';
import { useSmartBarPlacement } from '../hooks/useSmartBarPlacement';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
interface SelectionFloatingActionsProps {
  /** Ref to the SqlMonaco editor handle */
  editorRef: React.RefObject<SqlMonacoHandle | null>;
  /** Currently selected text (from onSelectionChange) */
  selectedText: string;
  /**
   * The full editor content. When `selectedText` covers the whole editor,
   * the floating Execute button is hidden — at that point it would be
   * redundant with the toolbar Execute button, which also runs the full content.
   */
  editorContent: string;
  /** Whether the main query is currently executing */
  isExecuting: boolean;
  /** Whether a peek query is currently executing */
  isPeekExecuting?: boolean;
  /** Run only the selected SQL (separate from the toolbar Execute, which runs the full editor) */
  onExecute?: (selectedSql: string) => void;
  /**
   * Preview the selected SQL inline without touching the main results grid.
   * The optional `anchorLine` argument is for callers that need to override
   * the default anchor line — this component doesn't use it (the peek hook
   * derives the anchor from the editor's current selection end line).
   */
  onPeek?: (selectedSql: string, anchorLine?: number) => void;
  /**
   * The selection covers exactly one whole VQE-editable statement (the host
   * decides), so an **Edit** button opens the compact editor on it.
   */
  canEdit?: boolean;
  onEdit?: () => void;
}

// Shared with CursorStatementActions so both bars align visually.
// Buttons "glue" to the selection by overlapping the anchor edge by 2px —
// the glued look with negative gap visually tucks the bar under the
// selection's rounded edge. `BUTTON_HEIGHT_WITH_GAP` = (button height 22)
// + (gap -2), giving the above-placement its pixel offset in one constant.
const BUTTON_HEIGHT_WITH_GAP = 20;
const BUTTON_BELOW_GAP = -2;

/**
 * Floating action bar that appears next to a Monaco selection. Offers:
 *
 * - **Execute** (runs only the selection) — visible when the selection looks
 *   like a complete, runnable statement AND differs from the full editor
 *   content (otherwise the toolbar Execute would do the same thing).
 * - **Peek** (safe inline preview) — visible when the selection is a
 *   read-only, result-producing statement (`isExecutableSelect`).
 * - **Edit** (compact Visual Query Editor) — visible when the selection is
 *   exactly one whole editable statement (`canEdit`, decided by the host).
 *
 * When no button qualifies, the whole bar is hidden.
 *
 * Placement (above / below / view-zone fallback) lives in
 * `useSmartBarPlacement` and is shared with `CursorStatementActions` so both
 * bars behave identically with respect to neighbor collisions — preventing
 * the bar from jumping around as the user extends/trims their selection.
 */
export function SelectionFloatingActions({
  editorRef,
  selectedText,
  editorContent,
  isExecuting,
  isPeekExecuting,
  onExecute,
  onPeek,
  canEdit = false,
  onEdit,
}: SelectionFloatingActionsProps) {
  // Evaluation is debounced so we don't thrash view-zone adds/removes during
  // rapid selection changes (drag-select, shift-arrow). `sql` + `range` only
  // update after the user lingers on a selection long enough.
  const [sql, setSql] = useState('');
  const [range, setRange] = useState<EditorSelectionRange | null>(null);
  const [isSelectionFullContent, setIsSelectionFullContent] = useState(false);
  // While the user is actively dragging a selection (mouse pressed inside the
  // editor), suppress the bar entirely so it doesn't flicker into view mid-
  // drag. It reappears after the pointer is released and the selection is
  // final. Keyboard selections (shift-arrow, shift-home/end, ctrl+a) don't
  // involve a pointer press, so they're unaffected.
  const [isPointerSelecting, setIsPointerSelecting] = useState(false);

  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      // Only treat presses inside a Monaco editor as the start of a drag-
      // select. A press on the floating bar itself (or anywhere else) must
      // not hide the bar — otherwise the user could never click Execute/Peek.
      const inMonaco = !!target.closest('.monaco-editor');
      const inBar = !!target.closest('[data-testid="selection-floating-actions"]');
      if (inMonaco && !inBar) {
        setIsPointerSelecting(true);
      }
    };
    const onPointerUp = () => setIsPointerSelecting(false);
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('pointerup', onPointerUp, true);
    // `pointercancel` covers edge cases where the browser aborts the
    // gesture (e.g. context menu, touch cancel) without firing pointerup.
    document.addEventListener('pointercancel', onPointerUp, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('pointerup', onPointerUp, true);
      document.removeEventListener('pointercancel', onPointerUp, true);
    };
  }, []);

  const canExecute = useMemo(
    () => isExecutableStatement(sql) && !isSelectionFullContent,
    [sql, isSelectionFullContent],
  );
  const canPeek = useMemo(() => isExecutableSelect(sql), [sql]);
  const showEdit = canEdit && !!onEdit;
  const hasAnyAction = canExecute || canPeek || showEdit;

  const evaluate = useCallback(() => {
    const editor = editorRef.current;
    if (isExecuting || !selectedText.trim() || !editor) {
      setRange(null);
      return;
    }
    const selectionRange = editor.getSelectionRange();
    if (!selectionRange) {
      setRange(null);
      return;
    }
    setSql(selectedText);
    setIsSelectionFullContent(selectedText.trim() === editorContent.trim());
    setRange(selectionRange);
  }, [editorRef, selectedText, editorContent, isExecuting]);

  const debouncedEvaluate = useDebouncedCallback(evaluate, 300);

  useEffect(() => {
    // Clear synchronously — not through the debounce — when there's no
    // selection to act on. Otherwise clicking to deselect would leave the
    // bar's stale `range` / `sql` in place for the debounce's 300ms,
    // which causes a flicker: pointerdown hides the bar, pointerup fires
    // before the debounce and reveals it again with stale state, then the
    // debounce finally settles and hides it for real. Any pending debounce
    // from an earlier selection change is cancelled so it can't overwrite
    // our synchronous reset.
    if (isExecuting || !selectedText.trim()) {
      debouncedEvaluate.cancel();
      setSql('');
      setRange(null);
      return;
    }
    debouncedEvaluate();
  }, [selectedText, editorContent, isExecuting, debouncedEvaluate]);

  const position = useSmartBarPlacement({
    editorRef,
    editorContent,
    range,
    // Disabling the hook during an active pointer-drag clears both the
    // placement state AND any active view zone, so the carved-out spacer
    // doesn't flash into existence mid-drag. Everything resumes on
    // pointerup (same as the bar itself).
    enabled: hasAnyAction && !isExecuting && !isPointerSelecting,
    buttonHeightWithGap: BUTTON_HEIGHT_WITH_GAP,
    belowGap: BUTTON_BELOW_GAP,
  });

  const handleExecuteClick = useCallback(() => {
    if (sql && onExecute) onExecute(sql);
  }, [sql, onExecute]);

  const handlePeekClick = useCallback(() => {
    if (sql && onPeek && !isPeekExecuting) onPeek(sql);
  }, [sql, onPeek, isPeekExecuting]);

  if (!position || !hasAnyAction || isPointerSelecting) return null;

  // Per-corner rounding, as in CursorStatementActions: the first visible
  // button rounds its left corner, the last its right. Edit is always last.
  const above = position.mode === 'above';
  const leftClass = above ? 'rounded-tl-md' : 'rounded-bl-md';
  const rightClass = above ? 'rounded-tr-md' : 'rounded-br-md';
  const fullClass = above ? 'rounded-t-md' : 'rounded-b-md';
  const onlyOne = [canExecute, canPeek, showEdit].filter(Boolean).length === 1;
  const executeRounding = onlyOne ? fullClass : leftClass;
  const peekIsLast = canPeek && !showEdit;
  const peekRounding = onlyOne
    ? fullClass
    : !canExecute && !peekIsLast
      ? leftClass
      : peekIsLast
        ? rightClass
        : '';
  const editRounding = onlyOne ? fullClass : rightClass;

  // Fade the 2px edge that touches the selection so the bar reads as
  // "growing out" of the highlighted text rather than sitting on top of it.
  // The gradient goes from opaque (interior) → transparent (touching edge),
  // letting the selection's tint bleed through the last 2px of the button.
  const edgeMask =
    position.mode === 'above'
      ? 'linear-gradient(to bottom, black, black calc(100% - 2px), transparent)'
      : 'linear-gradient(to bottom, transparent, black 2px, black)';

  return (
    <div
      className="absolute z-20 pointer-events-auto flex items-stretch gap-0"
      style={{
        top: position.top,
        left: position.left,
        maskImage: edgeMask,
        WebkitMaskImage: edgeMask,
      }}
      data-testid="selection-floating-actions"
    >
      {canExecute && (
        <button
          onClick={handleExecuteClick}
          // Fill is `--primary-alt` (U10) — matches the query-toolbar
          // Execute button and the gutter Execute button
          // (CursorStatementGutterActions), all sharing one grey fill token.
          // See src/index.css's --primary-alt comment and task-U10-report.md.
          //
          // U15 — full-strength `bg-primary-alt` (not `/85`), matching the
          // toolbar exactly. Hover is PINNED to `/90` — not omitted — per
          // this wave's recurring Tailwind-specificity trap (a scoped
          // `hover:` utility silently outranks a plain state class); see
          // QueryToolbar.tsx's Execute button comment.
          className={`flex items-center gap-1.5 px-2.5 py-[2px] text-xs font-medium
            bg-primary-alt text-primary-alt-foreground shadow-md
            hover:bg-primary-alt/90 transition-colors backdrop-blur-sm
            border border-primary-alt/20 ${executeRounding}`}
          data-testid="selection-execute-button"
          title="Execute selection"
        >
          {/* U15 — orange glyph, fill-aware not theme-aware: matches the
              toolbar's `text-energy-on-dark-fill` / `dark:text-energy-on-
              light-fill` pair. See QueryToolbar.tsx and src/index.css's
              --energy-on-*-fill tokens for the measured writeup. */}
          <TextSelect className="h-3 w-3 text-energy-on-dark-fill dark:text-energy-on-light-fill" />
          Execute
        </button>
      )}

      {canPeek && (
        <button
          onClick={handlePeekClick}
          disabled={isPeekExecuting}
          className={`flex items-center gap-1.5 px-2.5 py-[2px] text-xs font-medium
            bg-background/85 text-foreground shadow-md
            hover:bg-accent transition-colors backdrop-blur-sm
            border border-border disabled:opacity-70 ${peekRounding}`}
          data-testid="peek-results-button"
          title="Peek selection results"
        >
          {isPeekExecuting ? (
            <RandomDotmSpinner size={12} />
          ) : (
            <Eye className="h-3 w-3" />
          )}
          {isPeekExecuting ? 'Peeking...' : 'Peek'}
        </button>
      )}

      {showEdit && (
        <button
          onClick={onEdit}
          className={`flex items-center gap-1.5 px-2.5 py-[2px] text-xs font-medium
            bg-background/85 text-foreground shadow-md
            hover:bg-accent transition-colors backdrop-blur-sm
            border border-border ${editRounding}`}
          data-testid="vqe-edit-selection-button"
          title="Edit this statement visually"
        >
          <Pencil className="h-3 w-3" />
          Edit
        </button>
      )}
    </div>
  );
}
